import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import { RemoveIcon } from "../Icons";
import useEscapeKey from "@hooks/useEscapeKey";

const ConfirmDeleteModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Delete Project?",
  description = "You're about to delete 1 selected project.\nThis action cannot be undone. Do you want to continue?",
  cancelText = "Cancel",
  confirmText = "Yes, delete it",
  loading = false,
  "data-testid": dataTestId = "confirm-delete-modal",
}) => {
  const fieldValueTestId = (name) => `${dataTestId}-field-${name}-value`;
  const { colors } = useTheme();
  const { t } = useTranslation();

  cancelText = t("common.cancel-button");
  confirmText = t("common.yes-delete-it");

  useEscapeKey(onClose, isOpen && !loading);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (!loading) {
      onConfirm();
    }
  };

  const handleCancel = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-start sm:justify-center p-4 ${colors.bgBlurModal} backdrop-blur-[1px] overflow-x-auto`}
      data-testid={`${dataTestId}-overlay`}
    >
      <div
        className={`relative w-full max-w-md min-w-md ${colors.bgModal} transform overflow-hidden rounded-lg shadow-xl transition-all duration-200`}
        onClick={(e) => e.stopPropagation()}
        data-testid={dataTestId}
      >
        {/* Content */}
        <div className="p-6">
          {/* Icon and Title */}
          <div className="mb-4 flex items-center justify-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center">
              <RemoveIcon size={56} className={colors.iconDelete} />
            </div>
          </div>

          {/* Title */}
          <div className="mb-4 text-center">
            <h3 className={`text-lg font-semibold ${colors.textPrimary} mb-2 uppercase`}>{title}</h3>

            {/* Description */}
            <p className={`text-sm ${colors.textSecondary} whitespace-pre-line`} data-testid={fieldValueTestId("description")}>{description}</p>
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex flex-row gap-3 justify-center">
            {/* Cancel Button */}
            <button
              onClick={handleCancel}
              disabled={loading}
              className={`rounded-md border px-4 py-2 text-sm font-medium transition-colors ${colors.borderOutLineButton} ${colors.textActive} hover:${colors.bgPrimary} min-w-[100px]`}
              data-testid={`${dataTestId}-cancel-button`}
            >
              {cancelText}
            </button>

            {/* Confirm Delete Button */}
            <button
              onClick={handleConfirm}
              disabled={loading}
              className={`flex min-w-[100px] items-center justify-center rounded-md ${colors.bgButtonDanger} ${colors.bgButtonDangerHover} px-4 py-2 text-sm font-medium ${colors.textNormal} transition-colors focus:ring-2 ${colors.focusRingDanger} focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50`}
              data-testid={`${dataTestId}-confirm-button`}
            >
              {loading ? (
                <svg
                  className={`mr-2 -ml-1 h-4 w-4 animate-spin ${colors.textNormal}`}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : null}
              {loading ? t("common.deleting") : confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDeleteModal;
